import { Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ComboBox } from "~/components/common/combo-box";
import { Button, Input, Label } from "~/components/ui";
import type {
  AdvancedAlarmEnum,
  AdvancedAlarmItemDto,
  AdvancedAlarmLogicalOperatorEnum,
  NodeDto,
} from "~/types/api";
import type { FormOption } from "~/types/ui";
import {
  AdvancedAlarmValueTypeEnum,
  advancedAlarmValueTypeToString,
  conditionOperators,
  defaultAlarmCondition,
  reindexAlarmConditions,
} from "~/utils/alarm-util";

export default function AlarmConditionsFormField({
  value,
  onChange,
  options = [],
  logicalOperator,
}: {
  value: AdvancedAlarmItemDto[];
  onChange: (val: AdvancedAlarmItemDto[]) => void;
  options?: { label: string; value: NodeDto[] }[];
  logicalOperator?: AdvancedAlarmLogicalOperatorEnum;
}) {
  const { t } = useTranslation();
  const nodes =
    (options.find((x) => x.label == "nodes")?.value as NodeDto[]) ?? [];

  function updateItem(index: number, item: AdvancedAlarmItemDto) {
    onChange(value.with(index, item));
  }

  function addItem() {
    onChange(value.concat(defaultAlarmCondition(value.length)));
  }

  function removeItem(index: number) {
    onChange(reindexAlarmConditions(value.filter((_, i) => i !== index)));
  }

  return (
    <div className="flex flex-col gap-2">
      {value.map((item, index) => (
        <AlarmConditionRow
          key={index}
          item={item}
          index={index}
          logicalOperator={logicalOperator}
          nodes={nodes}
          onChange={(next) => updateItem(index, next)}
          onRemove={() => removeItem(index)}
        />
      ))}
      <Button
        variant="outline"
        type="button"
        onClick={addItem}
        className="self-start"
      >
        <Plus className="h-[1.2rem] w-[1.2rem] text-green-600 mr-1" />
        {t("common.add")}
      </Button>
    </div>
  );
}

function AlarmConditionRow({
  item,
  index,
  logicalOperator,
  nodes,
  onChange,
  onRemove,
}: {
  item: AdvancedAlarmItemDto;
  index: number;
  logicalOperator?: AdvancedAlarmLogicalOperatorEnum;
  nodes: NodeDto[];
  onChange: (val: AdvancedAlarmItemDto) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();

  const nodeOptions: FormOption<string>[] = nodes.map(
    (node) => ({ label: node.name, value: node.deveui }) as FormOption<string>,
  );
  const selectedNode = nodes.find((node) => node.deveui === item.device?.deveui);
  const keyOptions: FormOption<string>[] = (selectedNode?.sensors ?? []).map(
    (sensor) =>
      ({
        label: sensor.label ?? sensor.key ?? "",
        value: sensor.key ?? "",
      }) as FormOption<string>,
  );

  function updateDevice(deveui: string, key: string) {
    const node = nodes.find((n) => n.deveui === deveui);
    const sensor = node?.sensors?.find((s) => s.key === key);
    onChange({
      ...item,
      device: {
        type: AdvancedAlarmValueTypeEnum.SENSORVALUE,
        deveui,
        key,
        name: node?.name,
        keyLabel: sensor?.label ?? key,
      },
    });
  }

  function updateValueType(type: AdvancedAlarmEnum) {
    onChange({
      ...item,
      value: {
        type,
        value:
          type === AdvancedAlarmValueTypeEnum.EMPTY
            ? ""
            : (item.value?.value ?? ""),
      },
    });
  }

  const valueType = item.value?.type ?? AdvancedAlarmValueTypeEnum.TEXT;
  const valueTypeOptions: FormOption<number>[] = [
    AdvancedAlarmValueTypeEnum.TEXT,
    AdvancedAlarmValueTypeEnum.NUMBER,
    AdvancedAlarmValueTypeEnum.DATE,
    AdvancedAlarmValueTypeEnum.EMPTY,
  ].map(
    (type) =>
      ({
        label: advancedAlarmValueTypeToString(type),
        value: type,
      }) as FormOption<number>,
  );

  return (
    <div className="flex items-end gap-2 rounded-md border p-3 flex-wrap">
      <Label className="mb-2 font-semibold w-10">
        {index === 0
          ? t("alarms.conditions.if")
          : logicalOperator === 1
            ? t("alarms.conditions.and")
            : t("alarms.conditions.or")}
      </Label>

      <div className="w-40">
        <Label className="text-sm">{t("alarms.fields.tag")}</Label>
        <ComboBox
          value={item.device?.deveui ?? ""}
          options={nodeOptions}
          placeholder={t("alarms.placeholders.tag")}
          onChange={(val) => updateDevice(val as string, "")}
        />
      </div>

      <div className="w-40">
        <Label className="text-sm">{t("alarms.fields.tagDataKey")}</Label>
        <ComboBox
          value={item.device?.key ?? ""}
          options={keyOptions}
          placeholder={t("alarms.placeholders.tagDataKey")}
          disabled={!item.device?.deveui}
          onChange={(val) => updateDevice(item.device?.deveui ?? "", val as string)}
        />
      </div>

      <div>
        <Label className="text-sm">{t("alarms.conditions.operator")}</Label>
        <div className="flex gap-1">
          {conditionOperators.map((op) => (
            <Button
              key={op}
              type="button"
              variant={item.op === op ? "default" : "outline"}
              size="sm"
              onClick={() => onChange({ ...item, op })}
            >
              {op}
            </Button>
          ))}
        </div>
      </div>

      <div className="w-32">
        <Label className="text-sm">{t("alarms.conditions.valueType")}</Label>
        <ComboBox
          value={valueType}
          options={valueTypeOptions}
          onChange={(val) => updateValueType(val as AdvancedAlarmEnum)}
        />
      </div>

      <div className="w-32">
        <Label className="text-sm">{t("alarms.fields.value")}</Label>
        {valueType === AdvancedAlarmValueTypeEnum.EMPTY ? (
          <Input value={t("alarms.conditions.noValue")} disabled />
        ) : (
          <Input
            type={
              valueType === AdvancedAlarmValueTypeEnum.NUMBER
                ? "number"
                : valueType === AdvancedAlarmValueTypeEnum.DATE
                  ? "time"
                  : "text"
            }
            value={item.value?.value ?? ""}
            onChange={(e) =>
              onChange({
                ...item,
                value: { type: valueType, value: e.target.value },
              })
            }
          />
        )}
      </div>

      <Button
        variant="ghost"
        type="button"
        onClick={onRemove}
        aria-label={t("common.delete")}
      >
        <Trash2 className="h-[1.2rem] w-[1.2rem] text-red-600" />
      </Button>
    </div>
  );
}
